import React, { useCallback, useEffect } from 'react';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import ANALYTICS_ROOT_PATHS from '../../../analytics/paths';
import { avtalegiroSelector, useAccounts } from '../../../fetchHooks/useAccount';
import type { Account } from '../../../generated-types';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import useTranslation from '../../../hooks/useTranslation';
import { ANALYTICS_END, ANALYTICS_START, AnalyticsEvents, trackPageView } from '../../../services/analytics';
import { cancelPayment } from '../../../services/paymentService';
import { SigningContextProvider, useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { EikaModal } from '../../EikaModal';
import MultiformWrapper from '../MultiformWrapper/MultiformWrapper';
import { PaymentAndTransferForm } from '../PaymentAndTransferForm';
import { PaymentActionType } from './form/actions';
import { validKid } from './form/kidService';
import paymentReducer from './form/reducer';
import { paymentInitialState } from './form/state';
import useFormContext, { PaymentFormContext } from './form/useFormContext';
import useValidate from './form/validate';

import '../TransactionModal.scss';

interface Props {
    show: boolean;
    setShow: React.Dispatch<boolean>;
    account: Account | undefined;
    isImmediatePaymentFlow?: boolean;
}

const Payment: React.FC<Omit<Props, 'isImmediatePaymentFlow'>> = ({ show, setShow, account }) => {
    const translated = useTranslation();

    const { state, handleChange } = useFormContext();
    const { showSigningFlow, signingStatus, dispatch, dynamicLinkId, trackSigningEvent } = useSigningContext();

    const onClearPayment = useCallback(() => {
        if ((signingStatus === 'PROCESSING' || signingStatus === 'CREATED') && dynamicLinkId) {
            cancelPayment(dynamicLinkId);
        }
        handleChange({
            type: PaymentActionType.RESET,
        });
        dispatch({ type: Actions.RESET });
    }, [signingStatus, dynamicLinkId, handleChange, dispatch]);

    const onClosePayment = useCallback(() => {
        onClearPayment();
        setShow(false);
    }, [setShow, onClearPayment]);

    useEffect(() => {
        if (show) {
            trackSigningEvent(ANALYTICS_START);
            trackPageView(ANALYTICS_ROOT_PATHS.PAYMENT);
        } else {
            trackSigningEvent(ANALYTICS_END);
        }
    }, [show, trackSigningEvent]);

    useEffect(() => {
        if (!state.fromAccount && account) {
            handleChange({
                type: PaymentActionType.FROM_ACCOUNT_SELECTED,
                payload: account.accountNumber,
            });
        }
    }, [account, handleChange, state.fromAccount]);

    const showPaymentResult = useShowPaymentResult();
    const hideCloseButton = showSigningFlow && !showPaymentResult;
    const { data: avtalegiroAccounts = [] } = useAccounts({ select: avtalegiroSelector });

    const showAvtaleGiroProposal =
        state.toAccount?.avtaleGiroSupported &&
        avtalegiroAccounts.length > 0 &&
        validKid(state.toAccount.kidFormats, state.note);
    return (
        <EikaModal
            className="transaction-modal"
            open={show}
            setOpen={setShow}
            size="wide"
            ariaLabel={translated.payment}
            onClose={onClosePayment}
            timeOut={!showAvtaleGiroProposal && signingStatus === 'COMPLETED' ? 2500 : undefined}
            contentShouldStretch
            hideCloseButton={hideCloseButton}
            desktopMinHeight="small"
        >
            <PaymentAndTransferForm
                defaultFocusActive
                wrapInContext={['transfer']}
                account={account}
                transferForm={<MultiformWrapper transactionType="transfer" account={account} type="modal" />}
                paymentForm={
                    <MultiformWrapper
                        transactionType="payment"
                        account={account}
                        type="modal"
                        onClose={onClosePayment}
                    />
                }
            />
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsEvent={AnalyticsEvents.PAYMENT} analyticsCategory={ANALYTICS_CATEGORIES.PAYMENT}>
        <PaymentFormContext
            validateHook={useValidate}
            reducer={paymentReducer}
            initialState={{
                ...paymentInitialState,
                immediate: props.isImmediatePaymentFlow,
            }}
        >
            <Payment {...props} />
        </PaymentFormContext>
    </SigningContextProvider>
);
